if (!customElements.get('quick-add-modal')) {
  customElements.define(
    'quick-add-modal',
    class QuickAddModal extends ModalDialog {
      constructor() {
        super();
        this.modalContent = this.querySelector('[id^="QuickAddInfo-"]');

        this.addEventListener('product-info:loaded', ({ target }) => {
          target.addPreProcessCallback(this.preprocessHTML.bind(this));
        });
      }

      hide(preventFocus = false) {
        const cartNotification = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        if (cartNotification) cartNotification.setActiveElement(this.openedBy);
        this.modalContent.innerHTML = '';

        if (preventFocus) this.openedBy = null;
        super.hide();
      }

      show(opener) {
        opener.setAttribute('aria-disabled', true);
        opener.classList.add('loading');
        opener.querySelector('.loading__spinner').classList.remove('hidden');

        fetch(opener.getAttribute('data-product-url'))
          .then((response) => response.text())
          .then((responseText) => {
            const responseHTML = new DOMParser().parseFromString(responseText, 'text/html');
            const productElement = responseHTML.querySelector('product-info');

            this.preprocessHTML(productElement);
            HTMLUpdateUtility.setInnerHTML(this.modalContent, productElement.outerHTML);

            // The bundle-subscription widget arrives with the fetched product HTML but its
            // own script only runs on the product page, so wire it up here for the modal.
            this.initSubscriptionWidget();

            if (window.Shopify && Shopify.PaymentButton) {
              Shopify.PaymentButton.init();
            }
            if (window.ProductModel) window.ProductModel.loadShopifyXR();

            super.show(opener);
          })
          .finally(() => {
            opener.removeAttribute('aria-disabled');
            opener.classList.remove('loading');
            opener.querySelector('.loading__spinner').classList.add('hidden');
          });
      }

      /**
       * Initialise the bundle-subscription widget inside the modal: render prices/discounts
       * and wire up Purchase Options / Delivery Frequency so the `selling_plan` is set before
       * Add to Cart. Bound to the freshly injected elements on every modal open.
       */
      initSubscriptionWidget() {
        const widget = this.modalContent.querySelector('[data-bundle-subscription-widget]');
        if (!widget) return;

        const product = this.parseSubscriptionProduct(widget);
        this.renderSubscriptionPrices(widget, product);
        this.renderSubscriptionDiscounts(widget, product);

        widget.querySelectorAll('[data-bundle-subscription-selling-plan-group-input]').forEach((input) => {
          input.addEventListener('change', () => this.handleSubscriptionGroupChange(widget, input));
        });
        widget.querySelectorAll('[data-bundle-subscription-selling-plan-input]').forEach((input) => {
          input.addEventListener('change', () => this.setSubscriptionSellingPlan(widget, input.value));
        });

        const checkedGroup = widget.querySelector('[data-bundle-subscription-selling-plan-group-input]:checked');
        if (checkedGroup) this.handleSubscriptionGroupChange(widget, checkedGroup);
      }

      parseSubscriptionProduct(widget) {
        const element = widget.querySelector('[data-bundle-subscription-product-json]');
        if (!element) return null;
        try {
          return JSON.parse(element.innerHTML);
        } catch (error) {
          return null;
        }
      }

      subscriptionVariantId(widget) {
        const form = widget.closest('form[action$="/cart/add"]');
        const input = form && form.querySelector('[name="id"]');
        return input ? input.value : null;
      }

      setSubscriptionSellingPlan(widget, value) {
        const input = widget.querySelector('[data-bundle-subscription-selling-plan-id-input]');
        if (input) input.value = value || '';
      }

      handleSubscriptionGroupChange(widget, groupInput) {
        const groupId = groupInput.value;

        widget.querySelectorAll('[data-bundle-subscription-selling-plans-container]').forEach((container) => {
          const matches = container.dataset.bundleSubscriptionSellingPlanGroupId === groupId;
          container.classList.toggle('bundle-subscription-widget--hidden', !(matches && groupInput.checked));
        });

        if (groupId === 'once') {
          this.setSubscriptionSellingPlan(widget, '');
          return;
        }

        const checkedPlan = widget.querySelector(`input[name="bundle-subscription-selling-plan-${groupId}"]:checked`);
        this.setSubscriptionSellingPlan(widget, checkedPlan ? checkedPlan.value : '');
      }

      renderSubscriptionPrices(widget, product) {
        if (!product) return;
        const variantId = this.subscriptionVariantId(widget);
        const variant = (product.variants || []).find((v) => `${v.id}` === `${variantId}`);
        if (!variant) return;

        widget.querySelectorAll('[data-bundle-subscription-selling-plan]').forEach((planElement) => {
          const planId = planElement.dataset.bundleSubscriptionSellingPlanId;
          const allocation = (variant.selling_plan_allocations || []).find(
            (a) => `${a.selling_plan_id}` === `${planId}`
          );
          const priceElement = planElement.querySelector('[data-bundle-subscription-per-delivery-price]');
          if (allocation && priceElement) priceElement.innerHTML = this.formatSubscriptionMoney(allocation.per_delivery_price);
        });
      }

      renderSubscriptionDiscounts(widget, product) {
        if (!product) return;

        widget.querySelectorAll('[data-bundle-subscription-selling-plan-group]').forEach((groupElement) => {
          const groupId = groupElement.dataset.bundleSubscriptionSellingPlanGroupId;
          const group = product.selling_plan_groups.find((g) => g.id === groupId);
          if (!group) return;

          const discounts = group.selling_plans.map((plan) =>
            plan.price_adjustments.length
              ? { value: plan.price_adjustments[0].value, type: plan.price_adjustments[0].value_type }
              : { value: 0, type: '' }
          );
          const max = discounts.reduce((a, b) => (a.value > b.value ? a : b), { value: 0, type: '' });
          if (max.value === 0) return;

          const summaryElement = groupElement.querySelector('[data-bundle-subscription-group-discount-summary]');
          if (!summaryElement) return;

          const upTo = discounts.some((d) => d.value !== max.value);
          const amount = max.type === 'fixed_amount' ? this.formatSubscriptionMoney(max.value) : `${max.value}%`;
          summaryElement.innerHTML = `(Save ${upTo ? 'up to ' : ''}${amount})`;
        });
      }

      formatSubscriptionMoney(cents) {
        const element = document.querySelector('[data-bundle-subscription-money-format]');
        const format =
          (element && element.getAttribute('data-bundle-subscription-money-format')) ||
          (typeof Shopify !== 'undefined' && Shopify.money_format) ||
          '${{ amount }}';

        let amount = cents;
        if (typeof amount === 'string') amount = amount.replace('.', '');

        const placeholder = /\{\{\s*(\w+)\s*\}\}/;
        const withDelimiters = (number, precision, thousands = ',', decimal = '.') => {
          if (isNaN(number) || number === null) return '0';
          const parts = (number / 100.0).toFixed(precision).split('.');
          const dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, `$1${thousands}`);
          return dollars + (parts[1] ? decimal + parts[1] : '');
        };

        let value = '';
        switch ((format.match(placeholder) || [])[1]) {
          case 'amount_no_decimals':
            value = withDelimiters(amount, 0);
            break;
          case 'amount_with_comma_separator':
            value = withDelimiters(amount, 2, '.', ',');
            break;
          case 'amount_no_decimals_with_comma_separator':
            value = withDelimiters(amount, 0, '.', ',');
            break;
          case 'amount_no_decimals_with_space_separator':
            value = withDelimiters(amount, 0, ' ');
            break;
          case 'amount_with_apostrophe_separator':
            value = withDelimiters(amount, 2, "'");
            break;
          default:
            value = withDelimiters(amount, 2);
        }
        return format.replace(placeholder, value);
      }

      preprocessHTML(productElement) {
        productElement.classList.forEach((classApplied) => {
          if (classApplied.startsWith('color-') || classApplied === 'gradient')
            this.modalContent.classList.add(classApplied);
        });
        this.preventDuplicatedIDs(productElement);
        this.removeDOMElements(productElement);
        this.removeGalleryListSemantic(productElement);
        this.updateImageSizes(productElement);
        this.preventVariantURLSwitching(productElement);
      }

      preventVariantURLSwitching(productElement) {
        productElement.setAttribute('data-update-url', 'false');
      }

      removeDOMElements(productElement) {
        const pickupAvailability = productElement.querySelector('pickup-availability');
        if (pickupAvailability) pickupAvailability.remove();

        const productModal = productElement.querySelector('product-modal');
        if (productModal) productModal.remove();

        const modalDialog = productElement.querySelectorAll('modal-dialog');
        if (modalDialog) modalDialog.forEach((modal) => modal.remove());
      }

      preventDuplicatedIDs(productElement) {
        const sectionId = productElement.dataset.section;

        const oldId = sectionId;
        const newId = `quickadd-${sectionId}`;
        productElement.innerHTML = productElement.innerHTML.replaceAll(oldId, newId);
        Array.from(productElement.attributes).forEach((attribute) => {
          if (attribute.value.includes(oldId)) {
            productElement.setAttribute(attribute.name, attribute.value.replace(oldId, newId));
          }
        });

        productElement.dataset.originalSection = sectionId;
      }

      removeGalleryListSemantic(productElement) {
        const galleryList = productElement.querySelector('[id^="Slider-Gallery"]');
        if (!galleryList) return;

        galleryList.setAttribute('role', 'presentation');
        galleryList.querySelectorAll('[id^="Slide-"]').forEach((li) => li.setAttribute('role', 'presentation'));
      }

      updateImageSizes(productElement) {
        const product = productElement.querySelector('.product');
        const desktopColumns = product?.classList.contains('product--columns');
        if (!desktopColumns) return;

        const mediaImages = product.querySelectorAll('.product__media img');
        if (!mediaImages.length) return;

        let mediaImageSizes =
          '(min-width: 1000px) 715px, (min-width: 750px) calc((100vw - 11.5rem) / 2), calc(100vw - 4rem)';

        if (product.classList.contains('product--medium')) {
          mediaImageSizes = mediaImageSizes.replace('715px', '605px');
        } else if (product.classList.contains('product--small')) {
          mediaImageSizes = mediaImageSizes.replace('715px', '495px');
        }

        mediaImages.forEach((img) => img.setAttribute('sizes', mediaImageSizes));
      }
    }
  );
}
